import type { Composer } from 'vue-i18n'
import type { ActiveScratchCard } from '~/composables/useScratchCard'
import type { ScratchAudienceDecision, ScratchOpenSource } from '~/composables/useScratchFlowCore'
import { canAutoPrompt, readAndCountPageView, readScratchStore, storeBlocksAllCards, storeHasGivenEmail } from '~/utils/scratchStore'
import {
  clampDelaySeconds,
  isSuppressedPath,
  shouldDowngradeTimed,
  timedRemainingMs,
} from '~/utils/scratchTiming'

/**
 * Scratch to win: when the card opens by itself.
 *
 * Called once, from `ScratchTrigger.vue` (mounted for the whole visit inside
 * `app.vue`'s ClientOnly). Two triggers, chosen by the card:
 *
 * - **TIMED** — a timer counted from navigation start, paused while the tab is
 *   hidden, so the delay the merchandiser set is the delay the visitor
 *   experiences rather than that delay plus `delayHydration`.
 * - **EXIT_INTENT** — the newsletter popup's trap, ported: the pointer crossing
 *   the top edge (desktop), the app being backgrounded for a few seconds
 *   (touch), or going quiet for a minute. Scrolling never opens anything; it
 *   only arms the trap.
 *
 * Nothing is armed before the audience is known and the card is loaded, which
 * removes the old trap's race with the deferred auth fetch, and nothing opens
 * over another overlay, on checkout, on the auth pages, on a preview page, or
 * for a visitor who is carrying a prize code (they have already played).
 *
 * **No card active** (none for this locale, it has ended, or the fetch failed):
 * the newsletter popup (`useNewsletterPopup`) takes the card's place, on the
 * EXIT_INTENT trap it always had — for a logged-out visitor who has not answered
 * it and has not already given an e-mail through the game. One owner for both
 * means one unprompted popup per page load, whichever it is, and a card that
 * ends mid-visit (or turns up after a failed fetch) hands over cleanly.
 *
 * It talks to `useScratchFlowCore`, never `useScratchFlow`: this module is in
 * the entry chunk of every route, and opening a card — or the newsletter popup
 * in its place — is all it ever does.
 */

/**
 * Nothing fires until the visitor has spent this long on a *visible* page, or
 * has scrolled `ARM_SCROLL_PX` — whichever lands first. Someone who arrives and
 * immediately swings back to the tab bar never wanted the shop, and a page
 * opened into a background tab has not been looked at at all yet.
 */
const ARM_DELAY_MS = 5000
const ARM_SCROLL_PX = 300

/**
 * Touch only: backgrounding the app for at least this long counts as leaving.
 * Nothing can be drawn while they are gone, so the offer opens on their return —
 * which on a phone is the only moment it can be shown at all. A desktop tab
 * switch is not treated as a departure; the top-edge exit covers that.
 */
const AWAY_MS = 3000

/**
 * Last resort for the visitor no other signal reaches: still here, doing
 * nothing, for a full minute.
 */
const IDLE_MS = 60_000
const IDLE_POLL_MS = 5000

/** A timer that elapsed while something else was on screen retries this often. */
const RETRY_POLL_MS = 2000

// ---- One page load ----------------------------------------------------------

/** One unprompted opening per page load, whatever the visitor does with it. */
let autoOpenedThisPageLife = false
/** Client-side navigations so far: the SEO rule only applies to the landing view. */
let spaNavigations = 0
/** On `performance.now()`'s clock; 0 = the document's own navigation start. */
let navigationStartedAt = 0
/** Whether this page load is the first of the tab session; resolved once. */
let landingPageView: boolean | null = null

/**
 * Is some other dialog on screen?
 *
 * The search and filter drawers keep their `role="dialog"` in the DOM and
 * mark it with `aria-hidden`; the mobile nav and the cart use `inert`; the lightbox and
 * quick-add exist only while open; reka dialogs unmount or carry `data-state`.
 * The scratch overlay itself is excluded by its own `data-scratch-overlay`
 * marker — without that it would see itself and never open twice.
 */
export function anotherOverlayIsOpen(): boolean {
  for (const el of document.querySelectorAll('[role="dialog"],[role="alertdialog"]')) {
    if (el.closest('[data-scratch-overlay]'))
      continue
    if (el.getAttribute('data-state') === 'closed')
      continue
    if (el.closest('[aria-hidden="true"],[inert]'))
      continue
    return true
  }
  return false
}

interface CardTriggerContext {
  kind: 'card'
  card: ActiveScratchCard
  audience: ScratchAudienceDecision
  /** Vendure ids come through as string or number; only identity matters here. */
  customerId: string | number | null
  /** The SEO rule turned this TIMED card into an exit-intent one for this page view. */
  seoDowngraded: boolean
  /** A locale is a channel with its own card: switching it re-decides. */
  locale: string
}

/**
 * No card is active: the newsletter popup's trap. Armed once and kept across
 * client-side navigation — as the popup's own trap was — rather than re-armed
 * (with a fresh arming delay) on every click.
 */
interface NewsletterTriggerContext {
  kind: 'newsletter'
  locale: string
}

type TriggerContext = CardTriggerContext | NewsletterTriggerContext

export function useScratchTrigger(): void {
  const route = useRoute()
  const auth = useAuth()
  const flow = useScratchFlowCore()
  const scratchCard = useScratchCard()
  const drawers = useDrawers()
  const giftDialog = useGiftUnlockDialog()
  const { prize: urlPrize } = useUrlCoupon()
  const newsletter = useNewsletterPopup()
  // Off the plugin rather than `useI18n()`, as `useScratchCard` reads it: the
  // card cache is keyed by this same value.
  const i18n = useNuxtApp().$i18n as Composer | undefined
  const localeOf = () => String(i18n?.locale?.value ?? 'en')

  let ctx: TriggerContext | null = null
  let dispose: (() => void) | undefined
  /** Guards `start()` against its own awaits: only the newest run may arm. */
  let startSeq = 0

  /** What keeps EITHER popup shut right now, whichever this page load has armed. */
  function screenIsBusy(): boolean {
    if (autoOpenedThisPageLife || document.hidden)
      return true
    if (flow.state.value.open || newsletter.open.value || isSuppressedPath(route.path))
      return true
    if (drawers.value.cart.open || drawers.value.cart.adding || drawers.value.navigation.open)
      return true
    if (giftDialog.isOpen.value)
      return true
    // A visitor holding a personal prize code has already played this game.
    if (urlPrize.value)
      return true
    return anotherOverlayIsOpen()
  }

  function canOpenCardNow(card: CardTriggerContext): boolean {
    if (screenIsBusy())
      return false
    if ((auth?.activeCustomer.value?.id ?? null) !== card.customerId)
      return false
    // Also re-checks `endsAt`: `card` is null once the card has expired.
    if (String(scratchCard.card.value?.id ?? '') !== String(card.card.id))
      return false
    return canAutoPrompt(readScratchStore(), String(card.card.id), Date.now())
  }

  /**
   * The newsletter popup's own rules, as it always had them: logged-out only,
   * and not while its answer silences it (30 days after a dismissal, for good
   * after a sign-up). New: not on a `layout: false` page, where the popup is not
   * mounted (it would only surface on the next page, as a stale surprise); not
   * once a card has become live after all; and not for a browser that has given
   * its e-mail through the game already.
   */
  function canOpenNewsletterNow(): boolean {
    if (screenIsBusy() || route.meta.layout === false)
      return false
    if (!auth?.isAuthResolved.value || auth.activeCustomer.value)
      return false
    if (scratchCard.card.value)
      return false
    return !newsletter.isSilenced() && !storeHasGivenEmail(readScratchStore())
  }

  function stop(): void {
    dispose?.()
    dispose = undefined
  }

  function fire(source: Exclude<ScratchOpenSource, 'PREVIEW'>): boolean {
    if (!ctx)
      return false
    if (ctx.kind === 'newsletter') {
      if (!canOpenNewsletterNow())
        return false
      newsletter.show()
    }
    else {
      if (!canOpenCardNow(ctx))
        return false
      flow.openCard(ctx.card, { source, audience: ctx.audience })
    }
    autoOpenedThisPageLife = true
    stop()
    return true
  }

  /**
   * The timer. Counted from navigation start so the delay is not paid twice
   * (hydration is deferred up to 5-7 s), paused while the tab is hidden, and —
   * when it elapses at a moment the card may not open (a drawer is up, the
   * route is suppressed) — retried every couple of seconds rather than lost.
   */
  function armTimed(delayMs: number): () => void {
    let remaining = timedRemainingMs(delayMs, performance.now() - navigationStartedAt, document.hidden)
    let timer: ReturnType<typeof setTimeout> | undefined
    let retry: ReturnType<typeof setInterval> | undefined
    let startedAt = 0

    function elapsed(): void {
      timer = undefined
      remaining = 0
      if (fire('TIMED'))
        return
      retry ??= setInterval(() => {
        if (!document.hidden)
          fire('TIMED')
      }, RETRY_POLL_MS)
    }

    function run(): void {
      if (document.hidden || timer)
        return
      if (remaining <= 0) {
        elapsed()
        return
      }
      startedAt = performance.now()
      timer = setTimeout(elapsed, remaining)
    }

    function pause(): void {
      if (!timer)
        return
      clearTimeout(timer)
      timer = undefined
      remaining = Math.max(0, remaining - (performance.now() - startedAt))
    }

    function onVisibilityChange(): void {
      if (document.hidden)
        pause()
      else
        run()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    run()

    return () => {
      pause()
      if (retry) {
        clearInterval(retry)
        retry = undefined
      }
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }

  /**
   * The exit-intent trap, ported from the newsletter popup.
   *
   * No single signal covers how people actually leave, so this watches several
   * and takes whichever arrives first: the pointer crossing the top edge of the
   * viewport (desktop); the app being backgrounded (touch only — on a desktop a
   * tab switch is ordinary multitasking and the pointer already reports a real
   * departure); or going quiet altogether. A refusal leaves the trap armed.
   */
  function armExitIntent(): () => void {
    let finePointer = false
    let armed = false
    let armTimer: ReturnType<typeof setTimeout> | null = null
    let idlePoll: ReturnType<typeof setInterval> | null = null
    let hiddenAt = 0
    let lastActivityAt = 0

    function disarm(): void {
      armed = false
      if (armTimer) {
        clearTimeout(armTimer)
        armTimer = null
      }
      if (idlePoll) {
        clearInterval(idlePoll)
        idlePoll = null
      }
      document.removeEventListener('visibilitychange', onVisibilityChange)
      document.removeEventListener('mouseout', onMouseOut)
      document.removeEventListener('pointermove', onActivity)
      document.removeEventListener('keydown', onActivity)
      document.removeEventListener('touchstart', onActivity)
      window.removeEventListener('scroll', onScroll)
    }

    function trigger(): void {
      if (armed)
        fire('EXIT_INTENT')
    }

    function arm(): void {
      if (armTimer) {
        clearTimeout(armTimer)
        armTimer = null
      }
      armed = true
    }

    /** Only counts time the page is actually on screen, so background tabs don't age into it. */
    function startArming(): void {
      if (armed || armTimer || document.hidden)
        return
      armTimer = setTimeout(arm, ARM_DELAY_MS)
    }

    function onActivity(): void {
      lastActivityAt = Date.now()
    }

    function onMouseOut(event: MouseEvent): void {
      // `relatedTarget` is null only when the pointer left the document itself,
      // and clientY <= 0 narrows that to the top edge — where the tab strip and
      // the address bar are. Leaving sideways or downwards is ordinary.
      if (event.relatedTarget || event.clientY > 0)
        return
      trigger()
    }

    function onScroll(): void {
      onActivity()
      if (!armed && window.scrollY >= ARM_SCROLL_PX)
        arm()
    }

    function onVisibilityChange(): void {
      if (document.hidden) {
        // Only an armed page can have been "left" — one that loaded into a
        // background tab is simply not read yet.
        hiddenAt = armed ? Date.now() : 0
        if (armTimer) {
          clearTimeout(armTimer)
          armTimer = null
        }
        return
      }

      const away = hiddenAt ? Date.now() - hiddenAt : 0
      hiddenAt = 0
      lastActivityAt = Date.now()

      if (!finePointer && away >= AWAY_MS) {
        trigger()
        return
      }

      startArming()
    }

    function checkIdle(): void {
      if (document.hidden)
        return
      if (Date.now() - lastActivityAt >= IDLE_MS)
        trigger()
    }

    finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches
    lastActivityAt = Date.now()

    startArming()

    document.addEventListener('visibilitychange', onVisibilityChange)
    document.addEventListener('keydown', onActivity, { passive: true })
    document.addEventListener('touchstart', onActivity, { passive: true })
    // Watched on every device, but it never opens the offer: it only arms the
    // trap and reports the clearest sign of life for the idle rule.
    window.addEventListener('scroll', onScroll, { passive: true })

    if (finePointer) {
      document.addEventListener('mouseout', onMouseOut)
      // Watched purely as a sign of life for the idle rule — a reader who moves
      // the mouse but never scrolls is not idle.
      document.addEventListener('pointermove', onActivity, { passive: true })
    }

    if (IDLE_MS > 0)
      idlePoll = setInterval(checkIdle, IDLE_POLL_MS)

    return disarm
  }

  /**
   * No card is active for this locale: arms the newsletter popup's trap in its
   * place, unless the popup could not open for this visitor anyway (see
   * `canOpenNewsletterNow`). Left unarmed, `ctx` stays null and the next
   * navigation asks again — a login, a logout or a new card can change the answer.
   */
  function armNewsletter(): void {
    if (auth?.activeCustomer.value || newsletter.isSilenced() || storeHasGivenEmail(readScratchStore()))
      return
    ctx = { kind: 'newsletter', locale: localeOf() }
    dispose = armExitIntent()
  }

  /**
   * Works out whether this visitor may be shown a card, loads it, and arms the
   * trigger it asks for — or, with no card active, the newsletter popup's trap.
   * Every early return before the card is asked for costs nothing: a silenced
   * browser and a suppressed route never issue a single request.
   */
  async function start(): Promise<void> {
    const run = ++startSeq
    if (autoOpenedThisPageLife || isSuppressedPath(route.path))
      return

    stop()
    ctx = null

    if (storeBlocksAllCards(readScratchStore()))
      return

    const audience = await flow.resolveAudience()
    if (run !== startSeq)
      return
    if (!audience.show || urlPrize.value)
      return

    const card = await scratchCard.ensure()
    if (run !== startSeq)
      return
    if (!card) {
      armNewsletter()
      return
    }
    if (!canAutoPrompt(readScratchStore(), String(card.id), Date.now()))
      return

    const seoDowngraded = card.trigger === 'TIMED' && shouldDowngradeTimed({
      coarsePointer: window.matchMedia('(pointer: coarse)').matches,
      landingPageView: !!landingPageView && spaNavigations === 0,
      referrer: document.referrer,
    })

    ctx = {
      kind: 'card',
      card,
      audience,
      customerId: auth?.activeCustomer.value?.id ?? null,
      seoDowngraded,
      locale: localeOf(),
    }

    dispose = card.trigger === 'TIMED' && !seoDowngraded
      ? armTimed(clampDelaySeconds(card.delaySeconds) * 1000)
      : armExitIntent()
  }

  onMounted(() => {
    landingPageView ??= readAndCountPageView() === 0

    // A login or logout changes the whole decision, so it restarts it.
    watch(
      () => [auth?.isAuthResolved.value, auth?.activeCustomer.value?.id ?? null],
      ([resolved]) => {
        if (resolved)
          void start()
      },
      { immediate: true },
    )

    watch(() => route.path, (path, previous) => {
      if (path === previous)
        return
      spaNavigations++
      navigationStartedAt = performance.now()
      // `/preview/` is itself a suppressed path, so the preview page's own
      // overlay needs the same guard as the watchers below: without it a route
      // change would shut the overlay the owner came to look at.
      if (flow.state.value.open && !flow.state.value.preview && isSuppressedPath(path))
        flow.close('interrupted')
      // The newsletter popup lives in the default layout. Left open while the
      // visitor goes to a page without it, its state would pop it up again on
      // the next page that has one.
      if (newsletter.open.value && (route.meta.layout === false || isSuppressedPath(path)))
        newsletter.open.value = false
      // A search-landing card was downgraded to exit intent for that one view;
      // from the second page view it is a timer again, counted from here.
      if (ctx?.kind === 'card' && ctx.seoDowngraded && !autoOpenedThisPageLife) {
        stop()
        ctx = null
      }
      if (!ctx && auth?.isAuthResolved.value)
        void start()
      // The newsletter may have been armed because the card fetch FAILED. The
      // armed context keeps `start()` from re-running, so ask for the card here:
      // free while a real answer is cached, a refetch once a failed one is
      // older than its TTL — and a card that turns up hands over (watcher below).
      else if (ctx?.kind === 'newsletter' && !autoOpenedThisPageLife)
        void scratchCard.ensure()
    })

    // A locale is a channel with its own card, or none: switching it re-decides
    // between the card and the newsletter popup.
    watch(localeOf, (locale) => {
      if (!ctx || ctx.locale === locale || autoOpenedThisPageLife)
        return
      stop()
      ctx = null
      if (auth?.isAuthResolved.value)
        void start()
    })

    // A card that ends mid-visit hands the page over to the newsletter popup,
    // and a card that shows up takes it back (after a failed fetch is retried,
    // or on a locale switch — a successful "no card" answer is kept for the
    // page load) — unless something has already opened. Without this an ended
    // card kept its context armed and neither popup could open until a reload.
    watch(() => scratchCard.card.value?.id ?? null, (id, previous) => {
      if (id === previous || autoOpenedThisPageLife || !auth?.isAuthResolved.value)
        return
      if ((id === null && ctx?.kind === 'card') || (id !== null && ctx?.kind === 'newsletter')) {
        stop()
        ctx = null
        void start()
      }
    })

    watch(() => drawers.value.cart.open, (open) => {
      if (open && flow.state.value.open && !flow.state.value.preview)
        flow.close('interrupted')
    })

    // A card that ends while the overlay is open closes it on the first two
    // steps; from the e-mail step on the server answers NOT_AVAILABLE itself
    // (it accepts the played card for a grace period), which is a kinder place
    // to learn it than a screen vanishing mid-sentence.
    watch(() => scratchCard.card.value?.id ?? null, (id) => {
      const current = flow.state.value
      if (!current.open || current.preview)
        return
      if (current.cardId === (id == null ? null : String(id)))
        return
      if (current.step === 'SCRATCH' || current.step === 'REVEAL')
        flow.close('interrupted')
    })
  })

  onBeforeUnmount(() => {
    stop()
    startSeq++
  })
}
